import { section } from "@/glow";
import { qs, qsa, listen, add, remove, contains } from "@fluorescent/dom";
import Carousel from "@/lib/carousel";
import ProductItem from "@/lib/product-item";
import { animateListSlider, shouldAnimate } from "@/lib/animation";

const selectors = {
  navItem: "[js-navigation-list-item]",
  navItemByIndex: index =>
    `[js-navigation-list-item][data-navigation-item="${index}"]`,
  navButton: "[js-carousel-navigation-button]",
  navButtonByIndex: index =>
    `[js-carousel-navigation-button][data-navigation="${index}"]`,
  navButtonNext: "[js-carousel-navigation-button][data-next]",
  navButtonNextByIndex: index =>
    `[js-carousel-navigation-button][data-next][data-navigation="${index}"]`,
  navButtonPrev: "[js-carousel-navigation-button][data-prev]",
  navButtonPrevByIndex: index =>
    `[js-carousel-navigation-button][data-prev][data-navigation="${index}"]`,
  navButtons: "[js-carousel-navigation-buttons]",
  navButtonsByIndex: index =>
    `[js-carousel-navigation-buttons][data-navigation="${index}"]`,
  navigationWrapperByIndex: index => `[data-navigation="${index}"]`,
  sliderContainer: "[js-slider-container]",
  sliderContainerByIndex: index =>
    `[js-slider-container][data-collection="${index}"]`,
  sliderMeta: "[js-slider-meta]",
};

const classes = {
  fadeout: "fadeout",
  initReveal: "init-reveal",
  reveal: "reveal",
  selected: "selected",
  visible: "visible",
};

section("featured-collection-slider", {
  onLoad() {
    this.events = [];
    this.carousels = [];
    this._initCarousels();

    if (shouldAnimate(this.container)) {
      this.animateListSlider = animateListSlider(this.container);
    }
  },

  _initCarousels() {
    const { productsPerView, mobileProductsPerView } = this.container.dataset;
    this.perView = parseInt(productsPerView, 10);
    this.mobilePerView = parseInt(mobileProductsPerView, 10) * 1.05;
    // 1.05 factor gives us a "peek" without CSS hacks
    // TODO: encapsulate this in carousel instead of duplication wherever
    // we call on carousel.  Can also simplify the config that we pass in
    // to something like perViewSmall, perViewMedium, perViewLarge and same with
    // spaceBetween?

    this.productItem = ProductItem(this.container);
    this.sliderMeta = qs(selectors.sliderMeta, this.container);

    this.navItems = qsa(selectors.navItem, this.container);
    this.navItems.forEach(button =>
      this.events.push(
        listen(button, "click", this._handleNavButton.bind(this))
      )
    );

    // Depending on the section layout, nav buttons can either all be paired within wrapper containers, or they
    // can live as independent buttons.
    const navButtons = qsa(selectors.navButtons, this.container);

    if (navButtons.length) {
      this.navigationButtons = navButtons;
    } else {
      this.navigationButtons = qsa(selectors.navButton, this.container);
    }

    this.carouselsElements = qsa(selectors.sliderContainer, this.container);
    this.carouselsElements.forEach((container, index) => {
      const navigationWrapper = qs(
        selectors.navButtonsByIndex(index),
        this.container
      );
      let nextButton, prevButton;

      if (navigationWrapper) {
        nextButton = qs(selectors.navButtonNext, navigationWrapper);
        prevButton = qs(selectors.navButtonPrev, navigationWrapper);
      } else {
        nextButton = qs(selectors.navButtonNextByIndex(index), this.sliderMeta);
        prevButton = qs(selectors.navButtonPrevByIndex(index), this.sliderMeta);
      }

      this.carousels.push(
        Carousel(container, {
          slidesPerView: this.mobilePerView,
          spaceBetween: 13, // matches product grid
          navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
          },
          breakpoints: {
            720: {
              spaceBetween: 17, // matches product grid
              slidesPerView:
                this.perView === 5 ? this.perView - 1 : this.perView,
            },
            1200: {
              spaceBetween: 25, // matches product grid
              slidesPerView: this.perView,
            },
          },
        })
      );
    });
  },

  _handleNavButton(e) {
    e.preventDefault();
    const { navigationItem } = e.currentTarget.dataset;

    if (!contains(e.currentTarget, classes.selected)) {
      this._hideAll();
      this._show(parseInt(navigationItem, 10));
    }
  },

  _hideAll() {
    remove(this.navItems, classes.selected);
    remove(this.navigationButtons, classes.visible);

    remove(this.carouselsElements, classes.initReveal);
    remove(this.carouselsElements, classes.reveal);

    if (shouldAnimate(this.container)) {
      add(this.carouselsElements, classes.fadeout);
      setTimeout(() => {
        remove(this.carouselsElements, classes.visible);
      }, 300);
    } else {
      remove(this.carouselsElements, classes.visible);
    }
  },

  _show(index) {
    const navigationWrapper = qs(
      selectors.navButtonsByIndex(index),
      this.container
    );

    if (navigationWrapper) {
      add(navigationWrapper, classes.visible);
    } else {
      const navButtons = qsa(
        selectors.navButtonByIndex(index),
        this.sliderControls
      );

      navButtons.forEach(button => add(button, classes.visible));
    }

    if (this.navItems.length) {
      const navigationItem = qs(
        selectors.navItemByIndex(index),
        this.container
      );
      add(navigationItem, classes.selected);
    }

    const collection = qs(
      selectors.sliderContainerByIndex(index),
      this.container
    );

    if (shouldAnimate(this.container)) {
      add(collection, classes.fadeout);
      add(collection, classes.initReveal);

      setTimeout(() => {
        add(collection, classes.visible);
        remove(collection, classes.fadeout);

        setTimeout(() => {
          add(collection, classes.reveal);
        }, 50);
      }, 300);
    } else {
      add(collection, classes.visible);
    }
  },

  onUnload() {
    this.carousels.forEach(swiper => swiper.destroy());
    this.animateListSlider?.destroy();
    this.events.forEach(unsubscribe => unsubscribe());
  },

  onBlockSelect({ target }) {
    const { collection } = target.dataset;
    this._hideAll();
    this._show(parseInt(collection, 10));
  },
});
